import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map, switchMap } from 'rxjs';
import { LadingNote } from '../../work-orders/work-order-details/work-order.interfaces';
import { LadingPatch, NotesSaveResponse, PanelDetails } from './work-order-panel.models';
import { patchMatches, responseFailure } from './work-order-panel.helpers';

@Injectable({ providedIn: 'root' })
export class WorkOrderPanelService {
  private readonly detailsEndpoint = '/api/work_orders/work_order_details.php';
  private readonly updateEndpoint = '/api/work_orders/work_orders_grid_details.php';
  private readonly notesEndpoint = '/api/work_orders/details/process_lading_notes.php';

  constructor(private readonly http: HttpClient) {}

  load(ladingUuid: string): Observable<PanelDetails> {
    return this.http
      .get<PanelDetails>(this.detailsEndpoint, {
        params: { uuid: ladingUuid },
      })
      .pipe(
        map((details) => {
          if (details?.lading?.lading_uuid !== ladingUuid) {
            throw new Error('The work order could not be loaded. Please try again.');
          }
          return details;
        }),
      );
  }

  saveFields(ladingUuid: string, patch: LadingPatch): Observable<PanelDetails> {
    return this.http
      .post<unknown>(this.updateEndpoint, {
        lading_uuid: ladingUuid,
        ...patch,
      })
      .pipe(
        map((response) => {
          const failure = responseFailure(response);
          if (failure) throw new Error(failure);
        }),
        // The legacy endpoint can return application errors with HTTP 200. Confirm
        // the changed fields through the existing read endpoint before clearing drafts.
        switchMap(() => this.load(ladingUuid)),
        map((details) => {
          if (!patchMatches(details.lading, patch)) {
            throw new Error(
              'The saved values could not be confirmed. Your draft is still here; check Full Details before trying again.',
            );
          }
          return details;
        }),
      );
  }

  saveNote(ladingUuid: string, text: string, existing?: LadingNote): Observable<LadingNote[]> {
    const note = text.trim();
    // Empty strings (and "0") mean DELETE in this legacy endpoint. The panel
    // edits/adds notes only; never turn clearing an editor into a deletion.
    if (!note || note === '0') throw new Error('Enter a written note before saving.');

    return this.http
      .post<NotesSaveResponse>(this.notesEndpoint, {
        lading_uuid: ladingUuid,
        notes: [
          {
            note,
            lading_note_uuid: existing?.lading_note_uuid,
            contact_uuid: existing?.contact_uuid || undefined,
          },
        ],
      })
      .pipe(
        map((response) => {
          if (response?.success !== true || !Array.isArray(response.lading_notes)) {
            throw new Error(response?.message || response?.error || 'The note could not be saved.');
          }
          return [...response.lading_notes].sort((left, right) => {
            const leftTime = left.date_last_modified || left.date || '';
            const rightTime = right.date_last_modified || right.date || '';
            return rightTime.localeCompare(leftTime);
          });
        }),
      );
  }
}
